<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'
import RewardFormBlock from '@/views/reward/components/RewardFormBlock.vue'
import LegacyRewardModal from '@/views/reward/components/RewardModal.vue'
import NewRewardModal from '@/components/rewards/RewardModal.vue'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { Audience, LoyaltyReward, RewardType } from '@/types/types'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import {
  getValidityInDaysDisclaimer,
  normalizeMerchantRewardSavePayload
} from '@/components/rewards/utils/rewardHelpers'
import { getClaimLimitSummary } from '@/components/rewards/utils/offerRewardHydration'
import RewardClaimLimitBlock from '@/components/rewards/RewardClaimLimitBlock.vue'
import { resolveAudienceScopedStoreIds, resolveIncludedAudiences } from '@/utils/audienceStoreIds'
import useAudienceStoreScope from '@/composables/useAudienceStoreScope'

const props = defineProps<{
  selectedReward: (LoyaltyReward & { includedSegmentIds: number[]; excludedSegmentIds: number[] }) | null
  hasAllPartnersWithSegmentationSupport: boolean
  allCustomerLists: Audience[]
  dissociateOnCancel?: boolean
  isAddMode?: boolean
  hasCompatibleVoucherIntegration: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'selected-reward:saved'): void
}>()

const selectedReward = useVModel(props, 'selectedReward', emit)

const { t, d } = useI18n()
const router = useRouter()
const { getStoresByPermission } = useStore()
const { hasPermissionBySlug } = useAccess()

const { merchantClaimRewardNotification } = useMerchantNotificationConfigs()
const { saveLoyaltyProgramReward, dissociateLoyaltyProgramReward } = useRewardForm()
const { segmentTypes } = useAudience()
const notification = useNotification()
const { isPolite } = useMerchantTexts()

const canDissociateReward = hasPermissionBySlug('reward.delete')

const { flags } = useFeatureFlag()

const RewardModal = computed(() => {
  return flags.value['offer-engine']?.enabled ? NewRewardModal : LegacyRewardModal
})

const hasMessageError = ref<boolean>(false)
const hasPointsError = ref(false)
const pointsInputRef = ref<{ focus: () => void } | null>(null)
const isFidelitySettingTouched = ref(false)
const isNotificationTextCustomized = ref(false)

const hasFrequencyClaimLimit = ref(false)
const frequencyUnits = ref([
  {
    id: 1,
    text: t('pages.loyaltyPage.edit.claimLimitTimeUnit.days')
  },
  {
    id: 7,
    text: t('pages.loyaltyPage.edit.claimLimitTimeUnit.weeks')
  },
  {
    id: 30,
    text: t('pages.loyaltyPage.edit.claimLimitTimeUnit.months')
  }
])
const selectedFrequencyUnit = ref(1)
const frequencyTimesPerUnit = ref(1)

const isDeleteRewardModalOpen = ref(false)
const isDeletingReward = ref(false)

const errorNoVoucherVarText = t('pages.loyaltyPage.errors.errorNoVoucherVar', { voucher: '{{ voucher }}' })

const storesWithPermission = computed(() => getStoresByPermission('reward.read'))

const includedAudiences = computed(() =>
  resolveIncludedAudiences(props.allCustomerLists, selectedReward.value.includedSegmentIds ?? [])
)
const scopedStoreIds = computed(() => resolveAudienceScopedStoreIds(includedAudiences.value))

const includedSegmentsOptions = computed(() => {
  return props.allCustomerLists.filter(
    (segment) => !selectedReward.value.excludedSegmentIds?.some((id) => id === segment.id)
  )
})

const excludedSegmentsOptions = computed(() => {
  return props.allCustomerLists.filter(
    (segment) => !selectedReward.value.includedSegmentIds?.some((id) => id === segment.id)
  )
})

const validityInDaysDisclaimer = computed(() => {
  return getValidityInDaysDisclaimer(selectedReward.value.validityInDays, d, t)
})

const audienceWithColor = computed<
  {
    value: number
    label: string
    type: string
    color: ChipColor
    icon?: Icons
  }[]
>(() => {
  return props.allCustomerLists.map((audience) => {
    if (audience.frequency_type) {
      audience.type = 'typology'
    }

    return {
      value: audience.id,
      label: audience.name,
      type: segmentTypes.find((segment) => segment.id === audience.type)?.id,
      color: getAudienceColor(audience.type),
      icon: segmentTypes.find((segment) => segment.id === audience.type)?.icon
    }
  })
})

const isRewardNotificationEnabled = computed(() => {
  return merchantClaimRewardNotification?.value?.is_active ?? false
})

const customVariables = computed<CustomVars[]>(() => {
  return [
    {
      type: 'variable',
      code: 'reward',
      label: t('global.customVars.reward.name'),
      icon: 'HeroGiftIconSolid' as Icons,
      length: selectedReward.value?.name?.length,
      example: selectedReward.value.name
    },
    {
      type: 'webapp',
      code: 'rewardLink',
      label: t('global.customVars.rewardLink.name'),
      icon: 'HeroGiftIconSolid' as Icons,
      length: 16,
      example: 'pngo.fr/reward'
    },
    {
      type: 'variable',
      code: 'voucher',
      label: t('global.customVars.voucher.name'),
      icon: 'HeroReceiptPercentIconSolid',
      length: 10,
      example: 'XXXXXXXXXX',
      disabled: !selectedReward.value.rewardType?.isVoucher || !hasVoucherCompatibleTargetStore.value,
      disabledMessage: !selectedReward.value.rewardType?.isVoucher
        ? t('global.customVars.voucher.disabledMessageType')
        : t('global.customVars.voucher.disabledMessageTargetStore')
    }
  ]
})

const unsetStoresForNotification = computed(() => {
  if (!merchantClaimRewardNotification.value?.store_ids) {
    return selectedReward.value?.stores
  }
  return (
    selectedReward.value?.stores?.filter(
      (store) => !merchantClaimRewardNotification.value?.store_ids?.includes(store.id)
    ) ?? []
  )
})

const showNotificationTip = computed(() => {
  return !isRewardNotificationEnabled.value || unsetStoresForNotification.value.length > 0
})

const hasBlockingMessageError = computed(() => isRewardNotificationEnabled.value && hasMessageError.value)

const notificationTipText = computed(() => {
  return isRewardNotificationEnabled.value
    ? t('pages.loyaltyPage.edit.fields.rewardNotification.unsetStores', {
        stores: unsetStoresForNotification.value.map((store) => store.name).join(', ')
      })
    : t('pages.loyaltyPage.edit.fields.rewardNotification.description')
})

const hasVoucherCompatibleTargetStore = computed(() => {
  return props.hasCompatibleVoucherIntegration === true && (selectedReward.value.storeIds?.length ?? 0) > 0
})

const errorNoVoucherVar = computed(() => {
  return (
    selectedReward.value.rewardType?.isVoucher === true &&
    hasVoucherCompatibleTargetStore.value &&
    !/\{\{\s*voucher\s*\}\}/.test(selectedReward.value.notificationTextOverride ?? '')
  )
})

const getAudienceColor = (type: string) => {
  switch (type) {
    case 'typology':
      return 'violet'
    case 'dynamic':
      return 'indigo'
    case 'static':
      return 'orange'
  }
}

const addFrequencyClaimLimit = () => {
  selectedReward.value.claimLimitDays = 1
  selectedFrequencyUnit.value = 1
  hasFrequencyClaimLimit.value = true
  isFidelitySettingTouched.value = true
}

const removeFrequencyClaimLimit = () => {
  selectedReward.value.claimLimitDays = null
  hasFrequencyClaimLimit.value = false
  isFidelitySettingTouched.value = true
}

const updateFrequencyClaimLimit = () => {
  selectedReward.value.claimLimitDays = frequencyTimesPerUnit.value * selectedFrequencyUnit.value
  isFidelitySettingTouched.value = true
}

const updateSelectedFrequencyUnit = (event: number) => {
  selectedFrequencyUnit.value = event
  updateFrequencyClaimLimit()
}

const buildPayload = (reward: LoyaltyReward) => {
  if (!reward) return null

  const payload: any = {
    id: reward.id || null,
    rewardTypeId: reward.rewardType?.id || 1,
    name: reward.name,
    description: reward.description?.trim() || null,
    value: reward.value,
    externalId: reward.externalId,
    items: reward.items,
    source: reward.source || 'loyalty',

    voucherDiscountAmount: reward.voucherDiscountAmount || null,
    discountValuePercent: reward.discountValuePercent || null,
    minimumOrderAmount: reward.minimumOrderAmount || null,

    validityInDays: selectedReward.value?.validityInDays,

    storeIds: selectedReward.value.storeIds,
    points: selectedReward.value.points,
    includedSegmentIds: selectedReward.value?.includedSegmentIds,
    excludedSegmentIds: selectedReward.value?.excludedSegmentIds,
    notificationTextOverride: selectedReward.value.notificationTextOverride || null,
    claimLimitQuantity: selectedReward.value?.claimLimitQuantity > 0 ? selectedReward.value?.claimLimitQuantity : null,
    claimLimitDays: selectedReward.value?.claimLimitDays || null
  }

  if (reward.minimumOrderAmount !== undefined) {
    payload.minimumOrderAmount = reward.minimumOrderAmount
  }

  if (reward.isForSingleUse !== undefined) {
    payload.isForSingleUse = reward.isForSingleUse
  }

  if (reward.pictureUrl !== null) {
    payload.pictureUrl = reward.pictureUrl
  } else {
    delete payload.pictureUrl
  }

  if (flags.value['offer-engine']?.enabled) {
    normalizeMerchantRewardSavePayload(payload, reward)
  }

  return payload
}

const isPointsMissing = computed(() => {
  const points = selectedReward.value?.points as number | string | null | undefined
  return points === null || points === undefined || points === ''
})

const handlePointsInput = () => {
  isFidelitySettingTouched.value = true
  hasPointsError.value = false
}

const handleSaveReward = async (reward: LoyaltyReward) => {
  if (isPointsMissing.value) {
    hasPointsError.value = true
    pointsInputRef.value?.focus()
    return
  }

  try {
    if (!reward) {
      notification.create('error', {
        text: t('pages.reward.errors.rewardNotFound')
      })
      return
    }

    const payload = buildPayload(reward)

    if (!payload) return

    await saveLoyaltyProgramReward(payload)

    if (reward.id) {
      notification.create('success', {
        text: t('pages.reward.editReward.success')
      })
    } else {
      notification.create('success', {
        text: t('pages.reward.createReward.success')
      })
    }

    emit('selected-reward:saved')
  } catch {
    notification.create('error', {
      text: t('pages.reward.editReward.error')
    })
  }
}

const closeCreateEditRewardModal = async () => {
  if (props.dissociateOnCancel) {
    await dissociateLoyaltyProgramReward(selectedReward.value.id)
  }
  emit('close')
}

const handleDissociateRewardFromLoyaltyProgram = async (reward: LoyaltyReward) => {
  try {
    await dissociateLoyaltyProgramReward(reward.id)
    notification.create('success', {
      text: t('pages.reward.dissociateReward.success')
    })
    closeDeleteRewardModal()
    emit('selected-reward:saved')
  } catch {
    notification.create('error', {
      text: t('pages.reward.editReward.error')
    })
  }
}

const openDeleteRewardModal = async () => {
  isDeleteRewardModalOpen.value = true
}

const closeDeleteRewardModal = () => {
  isDeleteRewardModalOpen.value = false
}

const getDefaultTextDisplayed = (rewardType: RewardType) => {
  const value = isPolite.value ? 'valueFormal' : 'value'
  const isVoucherWithCompatibleTargetStore = rewardType?.isVoucher && hasVoucherCompatibleTargetStore.value

  return isVoucherWithCompatibleTargetStore
    ? t(`pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.${value}`, {
        reward: '{{ reward }}',
        voucher: '{{ voucher }}'
      })
    : t(`pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.${value}`, {
        reward: '{{ reward }}',
        qrcodeLink: '{{ rewardLink }}'
      })
}

const defaultTextDisplayed = computed(() => {
  return getDefaultTextDisplayed(selectedReward.value.rewardType)
})

const handleRewardTypeChanged = (reward: LoyaltyReward) => {
  selectedReward.value = {
    ...selectedReward.value,
    rewardType: reward.rewardType,
    ...(isNotificationTextCustomized.value
      ? {}
      : { notificationTextOverride: getDefaultTextDisplayed(reward.rewardType) })
  }
}

const handleNotificationTextInput = () => {
  isNotificationTextCustomized.value = true
  isFidelitySettingTouched.value = true
}

const handleSelectedStores = (storeIds: number[]) => {
  const currentStores = selectedReward.value.stores ?? []

  // Keep stores that are not in storesWithPermission (external stores)
  const externalStores = currentStores.filter(
    (store) => !storesWithPermission.value.some((permStore) => permStore.id === store.id)
  )

  // Get selected stores from storesWithPermission
  const selectedPermissionStores = storesWithPermission.value.filter((store) => storeIds.includes(store.id))

  // Combine external stores with selected permission stores
  selectedReward.value.stores = [...externalStores, ...selectedPermissionStores]

  // Update storeIds to match the stores array
  selectedReward.value.storeIds = selectedReward.value.stores.map((store) => store.id)

  isFidelitySettingTouched.value = true
}

useAudienceStoreScope({
  scopedStoreIds,
  getSelectedIds: () => selectedReward.value.storeIds ?? [],
  setSelectedIds: handleSelectedStores,
  getRestoreIds: () => [],
  emptyMeansUnrestricted: true
})

const handleMessageError = (error: boolean) => {
  hasMessageError.value = error
}

const claimLimitSummary = computed(() => {
  if (!selectedReward.value) return null
  return getClaimLimitSummary(
    {
      claimLimitQuantity: selectedReward.value.claimLimitQuantity,
      claimLimitDays: selectedReward.value.claimLimitDays
    },
    t
  )
})

onMounted(() => {
  const claimLimitDays = selectedReward.value?.claimLimitDays ?? 0
  hasFrequencyClaimLimit.value = claimLimitDays > 0
  if (claimLimitDays) {
    const FREQUENCY_UNITS = {
      WEEK: 7,
      MONTH: 30
    }

    frequencyTimesPerUnit.value = claimLimitDays
    if (claimLimitDays % FREQUENCY_UNITS.WEEK === 0) {
      selectedFrequencyUnit.value = FREQUENCY_UNITS.WEEK
      frequencyTimesPerUnit.value /= FREQUENCY_UNITS.WEEK
    } else if (claimLimitDays % FREQUENCY_UNITS.MONTH === 0) {
      selectedFrequencyUnit.value = FREQUENCY_UNITS.MONTH
      frequencyTimesPerUnit.value /= FREQUENCY_UNITS.MONTH
    }
  }

  if (!selectedReward.value.notificationTextOverride) {
    selectedReward.value.notificationTextOverride = defaultTextDisplayed.value
  } else if (selectedReward.value.notificationTextOverride !== defaultTextDisplayed.value) {
    isNotificationTextCustomized.value = true
  }
})
</script>

<template>
  <RewardModal
    :reward-id="selectedReward?.id"
    :is-external-form-modified="isFidelitySettingTouched"
    usage="loyalty"
    :is-add-mode="isAddMode"
    :has-errors="hasBlockingMessageError"
    @close="closeCreateEditRewardModal"
    @update:reward="handleSaveReward"
    @update:rewardTypeChanged="handleRewardTypeChanged"
  >
    <template #additional-form-blocks>
      <RewardFormBlock :title="t('pages.reward.detail.loyaltyProgramOptions')">
        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.loyaltyPage.edit.fields.points') }}</span>
            <span class="text-red-500">*</span>
          </label>

          <AppFormInput
            id="points"
            ref="pointsInputRef"
            v-model="selectedReward.points"
            type="number"
            step="1"
            :min="0"
            title="points"
            :errors="hasPointsError ? [t('pages.loyaltyPage.errors.pointsRequired')] : []"
            @update:model-value="handlePointsInput"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">
                {{ t('global.words.points') }}
              </div>
            </template>
          </AppFormInput>
        </div>

        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.loyaltyPage.edit.fields.validityInDays') }}</span>
          </label>

          <AppFormInput
            id="validityInDays"
            v-model="selectedReward.validityInDays"
            type="number"
            step="1"
            title="validityInDays"
            placeholder="∞"
            :min="0"
            :max="365"
            @update:model-value="isFidelitySettingTouched = true"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">
                {{ t('global.words.days') }}
              </div>
            </template>
          </AppFormInput>

          <p class="text-xs text-muted-foreground mt-1">{{ validityInDaysDisclaimer }}</p>
        </div>

        <div>
          <label class="mb-2 flex items-center gap-1 text-xs text-foreground">
            {{ t('pages.reward.availability.title') }}
          </label>

          <MultiSelectStores
            v-model="selectedReward.storeIds"
            :has-label="false"
            :max-shown-items="3"
            :stores-with-permission="storesWithPermission"
            :scoped-store-ids="scopedStoreIds"
            :additional-store-options="
              selectedReward.stores.map((store) => {
                return {
                  value: store.id,
                  label: store.name,
                  disabled: true
                }
              })
            "
            class="sm:w-full flex-1"
            @update:model-value="handleSelectedStores"
          />
        </div>

        <div
          :class="[
            'flex flex-col gap-2',
            {
              'border border-border rounded-md p-4': !hasAllPartnersWithSegmentationSupport
            }
          ]"
        >
          <BaseTip
            v-if="!hasAllPartnersWithSegmentationSupport"
            severity="secondary"
            :text="t('pages.loyaltyPage.edit.fields.storesIncludedDisclaimer')"
          />

          <div>
            <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
              <span>{{ t('pages.loyaltyPage.edit.fields.audienceIncluded') }}</span>
            </label>

            <MultiSelectAudiences
              v-model="selectedReward.includedSegmentIds"
              :disabled="!hasAllPartnersWithSegmentationSupport"
              :audiences="includedSegmentsOptions"
              hide-empty-categories
              @update:model-value="isFidelitySettingTouched = true"
            />
          </div>

          <div>
            <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
              <span>{{ t('pages.loyaltyPage.edit.fields.audienceExcluded') }}</span>
            </label>

            <MultiSelectAudiences
              v-model="selectedReward.excludedSegmentIds"
              :disabled="!hasAllPartnersWithSegmentationSupport"
              :audiences="excludedSegmentsOptions"
              hide-empty-categories
              @update:model-value="isFidelitySettingTouched = true"
            />
          </div>
        </div>

        <div v-if="!flags['offer-engine']?.enabled">
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            {{ t(`pages.loyaltyPage.edit.fields.claimLimit`) }}
          </label>
          <div class="flex flex-row justify-between space-x-2">
            <AppFormInput
              id="claimLimitQuantity"
              v-model="selectedReward.claimLimitQuantity"
              class="grow"
              name="claimLimitQuantity"
              type="number"
              placeholder="∞"
              :min="0"
              @update:model-value="isFidelitySettingTouched = true"
            >
              <template #icon-right>
                <div class="text-sm text-muted-foreground h-5 flex items-center justify-center">
                  {{ t('pages.loyaltyPage.edit.claimLimitTimes') }}
                </div>
              </template>
            </AppFormInput>
            <button
              v-if="!hasFrequencyClaimLimit"
              class="flex flex-row gap-1.5 items-center hover:text-primary/80 cursor-pointer text-sm font-medium text-foreground-secondary"
              @click="addFrequencyClaimLimit"
            >
              <BaseIcon name="HeroPlusIconOutline" class="h-5 w-5" />
              {{ t('pages.loyaltyPage.edit.addLimitClaimDays') }}
            </button>
            <div v-else class="flex-none">
              <div class="flex flex-row items-center justify-between space-x-2">
                <label>{{ t('pages.loyaltyPage.edit.fields.claimLimitDays') }}</label>
                <AppFormInput
                  id="claimLimitDays"
                  v-model="frequencyTimesPerUnit"
                  name="claimLimitDays"
                  type="number"
                  :min="1"
                  @change="updateFrequencyClaimLimit"
                ></AppFormInput>
                <ComboboxSelect
                  v-model="selectedFrequencyUnit"
                  value-key="id"
                  label-key="text"
                  trigger-class="w-24"
                  :options="frequencyUnits"
                  @update:model-value="updateSelectedFrequencyUnit($event)"
                />
                <button class="flex-none cursor-pointer">
                  <BaseIcon
                    name="HeroTrashIconSolid"
                    class="h-5 w-5 text-muted-foreground hover:text-red-700"
                    @click="removeFrequencyClaimLimit"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </RewardFormBlock>

      <RewardClaimLimitBlock
        v-if="flags['offer-engine']?.enabled && selectedReward"
        v-model:claim-limit-quantity="selectedReward.claimLimitQuantity"
        v-model:claim-limit-days="selectedReward.claimLimitDays"
        v-model:form-touched="isFidelitySettingTouched"
      />

      <RewardFormBlock :title="t('pages.reward.notifications.title')">
        <BaseTip v-if="showNotificationTip" severity="info">
          <template #text>
            <div class="flex items-center">
              <span class="mr-5 flex-1">
                {{ notificationTipText }}
              </span>
              <Button variant="link" @click="router.push({ name: 'TabletList', query: { activeTab: 'config' } })">
                {{ t('global.words.activate') }}
                <BaseIcon name="HeroArrowRightIconSolid" class="ml-1 h-5 w-5 text-blue-800" />
              </Button>
            </div>
          </template>
        </BaseTip>

        <div class="flex flex-col gap-2 w-full">
          <TextareaWithVars
            v-model="selectedReward.notificationTextOverride"
            :is-disabled="!isRewardNotificationEnabled"
            :label="t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.label')"
            :label-badge="t('pages.reward.notifications.tabletOnly')"
            :max-length="145"
            :i18n-strings="{
              textareaLabel: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.label'),
              characters: t('global.words.characters'),
              insertVar: t('global.customVars.insertLabel'),
              errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji')
            }"
            :custom-vars="customVariables"
            :sanitize-for-gsm="true"
            @has-error="handleMessageError"
            @update:model-value="handleNotificationTextInput"
          />

          <p v-if="errorNoVoucherVar" class="text-sm text-red-500">{{ errorNoVoucherVarText }}</p>
        </div>
      </RewardFormBlock>

      <RewardFormBlock
        v-if="selectedReward.id && canDissociateReward && !isAddMode"
        :title="t('pages.reward.detail.advancedOptions')"
      >
        <Button variant="outline-destructive" @click="openDeleteRewardModal()">
          {{ t('pages.reward.detail.dissociateFromLoyaltyProgram') }}
        </Button>
      </RewardFormBlock>
    </template>

    <template #additional-summary>
      <div class="rounded-lg flex flex-col bg-muted p-4">
        <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {{ t('pages.reward.detail.loyaltyOptions') }}
        </p>
        <ul class="space-y-1">
          <li class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary">
            <BaseIcon name="IconoirGift" class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="leading-[26px]">
              {{ t('pages.reward.detail.points') }} <span class="font-semibold">{{ selectedReward.points }}</span>
            </span>
          </li>

          <li class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary">
            <BaseIcon name="IconoirCalendar" class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="leading-[26px]">
              {{ t('pages.reward.detail.validityInDays') }}
              <span class="font-semibold">{{
                selectedReward.validityInDays
                  ? t('pages.reward.detail.days', { count: Number(selectedReward.validityInDays) })
                  : t('pages.reward.detail.infinite')
              }}</span>
            </span>
          </li>

          <li
            v-if="claimLimitSummary"
            class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
          >
            <BaseIcon
              :name="claimLimitSummary?.icon as Icons"
              class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground"
            />
            <span class="flex flex-wrap items-center gap-1 leading-[26px]">
              {{ claimLimitSummary?.text }}
            </span>
          </li>

          <li class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary">
            <BaseIcon name="IconoirShop" class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="flex flex-wrap items-center gap-1 leading-[26px]">
              {{ t('pages.loyaltyPage.edit.fields.storesIncluded') }}
              <StoreTooltip
                detail-expandable
                :stores="selectedReward.stores"
                :available-store-ids="storesWithPermission.map((store) => store.id)"
              />
            </span>
          </li>

          <li
            v-if="selectedReward.includedSegmentIds.length === 0 && selectedReward.excludedSegmentIds.length === 0"
            class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
          >
            <BaseIcon name="IconoirCommunity" class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="leading-[26px]">
              {{ t('pages.loyaltyPage.edit.fields.audienceIncluded') }}
              <span class="font-semibold">{{ t('pages.reward.detail.allCustomers') }}</span>
            </span>
          </li>

          <li
            v-if="selectedReward.includedSegmentIds.length > 0"
            class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
          >
            <BaseIcon name="IconoirCommunity" class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="flex flex-wrap items-center gap-1 leading-[26px]">
              {{ t('pages.loyaltyPage.edit.fields.audienceIncluded') }}
              <BaseChip
                v-for="segmentId in selectedReward.includedSegmentIds"
                :key="segmentId"
                :text="audienceWithColor.find((segment) => segment.value === segmentId)?.label"
                :color="audienceWithColor.find((segment) => segment.value === segmentId)?.color"
                :icon="audienceWithColor.find((segment) => segment.value === segmentId)?.icon"
              />
            </span>
          </li>

          <li
            v-if="selectedReward.excludedSegmentIds.length > 0"
            class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
          >
            <BaseIcon name="IconoirUserXmark" class="mt-[3px] h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="flex flex-wrap items-center gap-1 leading-[26px]">
              {{ t('pages.loyaltyPage.edit.fields.audienceExcluded') }}
              <BaseChip
                v-for="segmentId in selectedReward.excludedSegmentIds"
                :key="segmentId"
                :text="audienceWithColor.find((segment) => segment.value === segmentId)?.label"
                :color="audienceWithColor.find((segment) => segment.value === segmentId)?.color"
                :icon="audienceWithColor.find((segment) => segment.value === segmentId)?.icon"
              />
            </span>
          </li>
        </ul>
      </div>
    </template>
  </RewardModal>

  <BaseModal
    :is-open="isDeleteRewardModalOpen"
    :is-processing="isDeletingReward"
    :text-title="t('pages.loyaltyPage.confirm.modal.title')"
    :text="t('pages.loyaltyPage.confirm.modal.text')"
    :icon="'HeroTrashIconOutline'"
    :link-text="t('global.words.remove')"
    :icon-color="'red'"
    :cancel="t('global.words.cancel')"
    @close="closeDeleteRewardModal"
    @click="handleDissociateRewardFromLoyaltyProgram(selectedReward)"
  />
</template>
